import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

interface ErrorCardProps {
  error: Error | unknown;
  onRetry?: () => void;
  showHomeButton?: boolean;
  className?: string;
}

export function ErrorCard({ 
  error, 
  onRetry, 
  showHomeButton = true,
  className = ''
}: ErrorCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Extract error message
  const getErrorMessage = () => {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    if (error && typeof error === 'object' && 'message' in error) {
      return String(error.message);
    }
    return t('errors.unknown');
  };

  // Get appropriate error title based on error type
  const getErrorTitle = () => {
    const message = getErrorMessage().toLowerCase();
    
    if (message.includes('network') || message.includes('fetch')) {
      return t('errors.network.title');
    }
    if (message.includes('unauthorized') || message.includes('401')) {
      return t('errors.unauthorized.title');
    }
    if (message.includes('forbidden') || message.includes('403')) {
      return t('errors.forbidden.title');
    }
    if (message.includes('not found') || message.includes('404')) {
      return t('errors.notFound.title');
    }
    if (message.includes('server') || message.includes('500')) {
      return t('errors.server.title');
    }
    
    return t('errors.general.title');
  };

  return (
    <Card className={`border-red-200 bg-red-50 ${className}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-red-800">
          <AlertCircle className="h-5 w-5" />
          {getErrorTitle()}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-red-700">{getErrorMessage()}</p>
      </CardContent>
      <CardFooter className="flex gap-2">
        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="border-red-300 text-red-700 hover:bg-red-100"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('common.retry')}
          </Button>
        )}
        {showHomeButton && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/')}
            className="border-red-300 text-red-700 hover:bg-red-100"
          >
            <Home className="h-4 w-4 mr-2" />
            {t('common.home')}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

// Inline error display for forms or smaller areas
interface InlineErrorProps {
  error: Error | unknown;
  onRetry?: () => void;
  className?: string;
}

export function InlineError({ error, onRetry, className = '' }: InlineErrorProps) {
  const { t } = useTranslation();
  
  const getErrorMessage = () => {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return t('errors.unknown');
  };

  return (
    <div className={`flex items-center gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700 ${className}`}>
      <AlertCircle className="h-4 w-4 flex-shrink-0" />
      <span className="flex-1">{getErrorMessage()}</span>
      {onRetry && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRetry}
          className="h-auto p-1 text-red-700 hover:bg-red-100"
        >
          <RefreshCw className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}